// Cigar Advisor - Main React Application
import React, { useState, useEffect } from 'react';
import {
  Plus, Star, BarChart3, Menu, LogOut,
  ChevronLeft, Trash2, Edit,
  BookOpen, Heart, Home, MessageSquare, Map, Bookmark, Settings, MapPin,
  Download, Share2, Info, ExternalLink, X, Mic
} from 'lucide-react';
import { useAuth, useNotes, useStats, usePWA } from './hooks';
import { TastingNote, FlavorNote, Strength, UserPreferences } from './types';
import { CigarIcon } from './components/Icons';
import { FESTIVALS, SAMPLE_RECOMMENDATIONS, Festival, SAMPLE_VENDORS, FestivalCheckIn, FestivalNote, FestivalBadge, FESTIVAL_BADGES } from './data/festivals';
import { SAMPLE_LOUNGES, Lounge, LoungeHours, Badge, BADGES, CheckIn } from './data/lounges';

// Import views
import { LandingView } from './views/LandingView';
import { DashboardView } from './views/DashboardView';
import CigarAdvisorHub from './views/CigarAdvisorHub';
import { RecommendationsView } from './views/RecommendationsView';
import { SavedItemsView } from './views/SavedItemsView';
import { FestivalMapView } from './views/FestivalMapView';
import { FestivalDetailView } from './views/FestivalDetailView';
import { PreferencesQuizView } from './views/PreferencesQuizView';
import LoungeFinderView from './views/LoungeFinderView';
import LoungeDetailView from './views/LoungeDetailView';
import CheckInModalView from './views/CheckInModalView';
import SevenCLPromoView from './views/SevenCLPromoView';
import { AppFooter } from './components/AppFooter';
import { CigarExplorerView } from './views/CigarExplorerView';
import { MyHumidorView } from './views/MyHumidorView';
import { CityGuideView } from './views/CityGuideView';
import { BandGalleryView } from './views/BandGalleryView';
import { Cigar } from './data/cigars';
import { LogSmokeModal, SmokeLogView } from './components/SmokeLog';
import { InstallPrompt } from './components/InstallPrompt';
import { VoiceModal } from './components/VoiceModal';
import { CigarIdentifyResult } from './components/CigarIdentifyResult';
import { parseVoiceIntent } from './utils/voiceIntent';
import {
  TasteProfile,
  SmokeLogEntry,
  buildTasteProfile,
  getSmokeLog,
  deleteSmokeLogEntry,
  getRecentlySmokedIds,
} from './data/personalization';

// Constants
const FLAVOR_OPTIONS: FlavorNote[] = [
  'earthy', 'spicy', 'sweet', 'woody', 'creamy', 'nutty',
  'peppery', 'leather', 'cedar', 'coffee', 'cocoa', 'floral',
  'hay', 'toast', 'caramel', 'vanilla', 'citrus', 'herbal'
];

const STRENGTH_OPTIONS: Strength[] = ['mild', 'medium', 'medium-full', 'full'];


// Helper to get photo URL (handles both API paths and guest localStorage keys)
const getPhotoUrl = (key: string | undefined): string | null => {
  if (!key) return null;
  if (key.startsWith('guest_photo_')) {
    return localStorage.getItem(key);
  }
  return `/api/photos/${key}`;
};

// Local storage helpers for saved items
const getSavedQuestions = (): string[] => JSON.parse(localStorage.getItem('cigar_saved_questions') || '[]');
const saveSavedQuestions = (questions: string[]) => localStorage.setItem('cigar_saved_questions', JSON.stringify(questions));
const getSavedCigarIds = (): string[] => JSON.parse(localStorage.getItem('cigar_saved_cigars') || '[]');
const saveSavedCigarIds = (ids: string[]) => localStorage.setItem('cigar_saved_cigars', JSON.stringify(ids));
const getSavedVendorIds = (): string[] => JSON.parse(localStorage.getItem('cigar_saved_vendors') || '[]');
const saveSavedVendorIds = (ids: string[]) => localStorage.setItem('cigar_saved_vendors', JSON.stringify(ids));
const getFestivalCheckIns = (): FestivalCheckIn[] => JSON.parse(localStorage.getItem('cigar_festival_checkins') || '[]');
const saveFestivalCheckIns = (checkIns: FestivalCheckIn[]) => localStorage.setItem('cigar_festival_checkins', JSON.stringify(checkIns));
const getEarnedFestivalBadgeIds = (): string[] => JSON.parse(localStorage.getItem('cigar_festival_badges') || '[]');
const saveEarnedFestivalBadgeIds = (ids: string[]) => localStorage.setItem('cigar_festival_badges', JSON.stringify(ids));
const getFestivalNotes = (): FestivalNote[] => JSON.parse(localStorage.getItem('cigar_festival_notes') || '[]');
const saveFestivalNotes = (notes: FestivalNote[]) => localStorage.setItem('cigar_festival_notes', JSON.stringify(notes));
const getUserPreferences = (): UserPreferences | null => {
  const stored = localStorage.getItem('cigar_user_preferences');
  return stored ? JSON.parse(stored) : null;
};
const saveUserPreferences = (prefs: UserPreferences) => localStorage.setItem('cigar_user_preferences', JSON.stringify(prefs));

// Lounge check-in storage helpers
const getCheckIns = (): CheckIn[] => JSON.parse(localStorage.getItem('cigar_lounge_checkins') || '[]');
const saveCheckIns = (checkIns: CheckIn[]) => localStorage.setItem('cigar_lounge_checkins', JSON.stringify(checkIns));
const getEarnedBadgeIds = (): string[] => JSON.parse(localStorage.getItem('cigar_earned_badges') || '[]');
const saveEarnedBadgeIds = (ids: string[]) => localStorage.setItem('cigar_earned_badges', JSON.stringify(ids));

// ============ HEADER COMPONENT ============
const Header: React.FC<{
  user: any;
  onLogout: () => void;
  currentView: string;
  onNavigate: (view: string) => void;
  onOpenSettings: () => void;
  onOpenVoice: () => void;
}> = ({ user, onLogout, currentView, onNavigate, onOpenSettings, onOpenVoice }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-30 border-b border-white/10 bg-stone-900/90 backdrop-blur-lg">
      <div className="flex items-center justify-between px-4 py-3">
        <button onClick={() => onNavigate('dashboard')} className="flex items-center gap-2">
          <CigarIcon className="w-6 h-6 text-amber-500" />
          <span className="text-lg font-bold text-white">Ring & Ash</span>
        </button>

        {user && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onNavigate('new')}
              className="p-2 bg-amber-500 rounded-full text-stone-900 hover:bg-amber-400"
            >
              <Plus className="w-5 h-5" />
            </button>
            <button
              onClick={onOpenVoice}
              className="p-2 rounded-lg hover:bg-white/10 text-white"
              aria-label="Voice input"
            >
              <Mic className="w-5 h-5" />
            </button>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 rounded-lg hover:bg-white/10"
            >
              <Menu className="w-5 h-5 text-white" />
            </button>
          </div>
        )}
      </div>

      {menuOpen && user && (
        <div className="absolute right-2 top-14 w-56 bg-stone-800 border border-white/10 rounded-xl shadow-xl z-50">
          <div className="p-3 border-b border-white/10">
            <p className="text-white font-medium text-sm">{user.name}</p>
            <p className="text-white/50 text-xs">{user.email || 'Guest'}</p>
          </div>
          <nav className="p-2">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: Home },
              { id: 'advisor', label: 'Cigar Advisor', icon: MessageSquare },
              { id: 'journal', label: 'My Journal', icon: BookOpen },
              { id: 'lounges', label: 'Find Lounges', icon: MapPin },
              { id: 'recommendations', label: 'Recommendations', icon: Star },
              { id: 'saved', label: 'Saved Items', icon: Bookmark },
              { id: 'festivals', label: 'Festivals', icon: Map },
              { id: 'stats', label: 'Statistics', icon: BarChart3 },
              { id: 'preferences', label: 'Taste Profile', icon: Heart },
            ].map(item => (
              <button
                key={item.id}
                onClick={() => { onNavigate(item.id); setMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                  currentView === item.id
                    ? 'bg-amber-500/20 text-amber-500'
                    : 'text-white/70 hover:bg-white/10'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            ))}
          </nav>
          <div className="p-2 border-t border-white/10 space-y-1">
            <button
              onClick={() => { onOpenSettings(); setMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/10"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
            <button
              onClick={() => { onLogout(); setMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-white/10"
            >
              <LogOut className="w-4 h-4" />
              Log Out
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

// ============ SETTINGS MODAL ============
const SettingsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  pwa: { canInstall: boolean; isInstalled: boolean; isIOS: boolean; promptInstall: () => Promise<boolean> };
}> = ({ isOpen, onClose, pwa }) => {
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);

  if (!isOpen) return null;

  const handleInstall = async () => {
    if (pwa.isIOS) {
      setShowIOSInstructions(true);
    } else {
      await pwa.promptInstall();
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Ring & Ash - Cigar Companion',
      text: 'Your personal cigar advisor, journal, and lounge finder. Discover the perfect cigar for any occasion.',
      url: 'https://ringandash.com',
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareData.url);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
      }
    } catch (err) {
      // User cancelled share
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm mx-4 bg-stone-800 rounded-2xl border border-white/10 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">Settings</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10">
            <X className="w-5 h-5 text-white/70" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Install App */}
          {pwa.canInstall && (
            <button
              onClick={handleInstall}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 transition-colors"
            >
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Download className="w-5 h-5 text-amber-500" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">Install App</p>
                <p className="text-white/50 text-sm">Add to your home screen</p>
              </div>
            </button>
          )}

          {pwa.isInstalled && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-green-500/20">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Download className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-left">
                <p className="text-green-400 font-medium">App Installed</p>
                <p className="text-white/50 text-sm">Running as standalone app</p>
              </div>
            </div>
          )}

          {/* Share App */}
          <button
            onClick={handleShare}
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
          >
            <div className="p-2 bg-white/10 rounded-lg">
              <Share2 className="w-5 h-5 text-white/70" />
            </div>
            <div className="text-left flex-1">
              <p className="text-white font-medium">Share App</p>
              <p className="text-white/50 text-sm">Invite friends to Ring & Ash</p>
            </div>
            {shareSuccess && (
              <span className="text-green-400 text-sm">Link copied!</span>
            )}
          </button>

          {/* About */}
          <div className="p-3 rounded-xl bg-white/5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-lg">
                <Info className="w-5 h-5 text-white/70" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">About Ring & Ash</p>
                <p className="text-white/50 text-sm">Version 1.0.0</p>
              </div>
            </div>
            <p className="mt-3 text-white/60 text-sm leading-relaxed">
              Your personal cigar companion. Discover cigars, track your tastings,
              find nearby lounges, and prepare for festivals with AI-powered recommendations.
            </p>
          </div>

          {/* 7CL Products */}
          <a
            href="https://7centline.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-stone-700/50 to-amber-900/30 hover:from-stone-700/70 hover:to-amber-900/50 transition-colors border border-amber-500/20"
          >
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <ExternalLink className="w-5 h-5 text-amber-500" />
            </div>
            <div className="text-left flex-1">
              <p className="text-white font-medium">7CL Products</p>
              <p className="text-amber-400/70 text-sm">Post-smoke wardrobe restoration</p>
            </div>
          </a>
        </div>

        {/* iOS Instructions Modal */}
        {showIOSInstructions && (
          <div className="absolute inset-0 bg-stone-800 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Install on iOS</h3>
              <button onClick={() => setShowIOSInstructions(false)} className="p-1 rounded-lg hover:bg-white/10">
                <X className="w-5 h-5 text-white/70" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-500 font-bold text-sm">1</span>
                </div>
                <p className="text-white/80">Tap the <strong className="text-white">Share</strong> button at the bottom of Safari</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-500 font-bold text-sm">2</span>
                </div>
                <p className="text-white/80">Scroll down and tap <strong className="text-white">"Add to Home Screen"</strong></p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-500 font-bold text-sm">3</span>
                </div>
                <p className="text-white/80">Tap <strong className="text-white">"Add"</strong> to install Ring & Ash</p>
              </div>
            </div>
            <button
              onClick={() => setShowIOSInstructions(false)}
              className="w-full mt-6 py-3 bg-amber-500 text-stone-900 font-medium rounded-xl hover:bg-amber-400 transition-colors"
            >
              Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ BOTTOM NAVIGATION ============
const BottomNav: React.FC<{
  currentView: string;
  onNavigate: (view: string) => void;
}> = ({ currentView, onNavigate }) => {
  const navItems = [
    { id: 'dashboard', label: 'Home', icon: Home },
    { id: 'advisor', label: 'Advisor', icon: MessageSquare },
    { id: 'lounges', label: 'Lounges', icon: MapPin },
    { id: 'journal', label: 'Journal', icon: BookOpen },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 pb-safe">
      {/* PWA Install/Share Footer */}
      <AppFooter className="border-t-0" />
      {/* Navigation */}
      <nav className="bg-stone-900/95 backdrop-blur-lg border-t border-white/10">
        <div className="flex justify-around items-center py-2">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
                currentView === item.id
                  ? 'text-amber-500'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
};

// ============ NOTE CARD COMPONENT ============
const NoteCard: React.FC<{
  note: TastingNote;
  onClick: () => void;
}> = ({ note, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white/5 rounded-xl border border-white/10 overflow-hidden hover:bg-white/10 cursor-pointer transition-colors"
  >
    {note.photoCigar && getPhotoUrl(note.photoCigar) && (
      <div
        className="h-32 bg-cover bg-center"
        style={{ backgroundImage: `url(${getPhotoUrl(note.photoCigar)})` }}
      />
    )}
    <div className="p-4">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="font-semibold text-white">{note.brand}</h3>
          {note.line && <p className="text-white/50 text-sm">{note.line}</p>}
        </div>
        {note.overallScore && (
          <div className="flex items-center gap-1 text-amber-500">
            <Star className="w-4 h-4 fill-current" />
            <span className="font-medium">{note.overallScore}</span>
          </div>
        )}
      </div>
      {note.flavors.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {note.flavors.slice(0, 3).map(flavor => (
            <span key={flavor} className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-500">
              {flavor}
            </span>
          ))}
        </div>
      )}
      <p className="text-white/40 text-xs mt-2">{note.smokeDate}</p>
    </div>
  </div>
);

// ============ JOURNAL VIEW ============
const JournalView: React.FC<{
  notes: TastingNote[];
  loading: boolean;
  onSelectNote: (id: string) => void;
  onNewNote: () => void;
  onBack: () => void;
}> = ({ notes, loading, onSelectNote, onNewNote, onBack }) => (
  <div className="flex-1 overflow-y-auto pb-20">
    <div className="sticky top-0 z-10 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
      <div className="flex items-center justify-between px-4 py-3">
        <button onClick={onBack} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <h1 className="text-lg font-semibold text-white">My Journal</h1>
        <button onClick={onNewNote} className="p-2 bg-amber-500 rounded-lg text-stone-900">
          <Plus className="w-5 h-5" />
        </button>
      </div>
    </div>

    <div className="p-4">
      {loading ? (
        <div className="text-center py-12 text-white/50">Loading...</div>
      ) : notes.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <h3 className="text-white font-medium mb-2">No tasting notes yet</h3>
          <p className="text-white/50 mb-4">Start logging your cigar experiences</p>
          <button
            onClick={onNewNote}
            className="px-6 py-2 bg-amber-500 text-stone-900 rounded-xl font-medium"
          >
            Add First Note
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {notes.map(note => (
            <NoteCard key={note.id} note={note} onClick={() => onSelectNote(note.id)} />
          ))}
        </div>
      )}
    </div>
  </div>
);

// ============ NOTE FORM VIEW (Simplified) ============
const NoteFormView: React.FC<{
  editNote?: TastingNote | null;
  onSave: (note: Partial<TastingNote>) => void;
  onCancel: () => void;
}> = ({ editNote, onSave, onCancel }) => {
  const [brand, setBrand] = useState(editNote?.brand || '');
  const [line, setLine] = useState(editNote?.line || '');
  const [strength, setStrength] = useState<Strength>(editNote?.overallStrength || 'medium');
  const [flavors, setFlavors] = useState<FlavorNote[]>(editNote?.flavors || []);
  const [score, setScore] = useState(editNote?.overallScore || 85);
  const [notes, setNotes] = useState(editNote?.notes || '');
  const [wouldBuyAgain, setWouldBuyAgain] = useState(editNote?.wouldBuyAgain || false);

  const handleSubmit = () => {
    if (!brand.trim()) return;
    onSave({
      brand: brand.trim(),
      line: line.trim() || undefined,
      overallStrength: strength,
      flavors,
      overallScore: score,
      notes: notes.trim() || undefined,
      wouldBuyAgain,
      smokeDate: editNote?.smokeDate || new Date().toISOString().split('T')[0],
    });
  };

  const toggleFlavor = (flavor: FlavorNote) => {
    if (flavors.includes(flavor)) {
      setFlavors(flavors.filter(f => f !== flavor));
    } else {
      setFlavors([...flavors, flavor]);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto pb-20">
      <div className="sticky top-0 z-10 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={onCancel} className="text-white/70">Cancel</button>
          <h1 className="text-lg font-semibold text-white">
            {editNote ? 'Edit Note' : 'New Note'}
          </h1>
          <button
            onClick={handleSubmit}
            disabled={!brand.trim()}
            className="text-amber-500 font-medium disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Brand & Line */}
        <div className="space-y-4">
          <div>
            <label className="text-white/60 text-xs mb-1 block">Brand *</label>
            <input
              type="text"
              value={brand}
              onChange={e => setBrand(e.target.value)}
              placeholder="e.g., Arturo Fuente"
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-amber-500"
            />
          </div>
          <div>
            <label className="text-white/60 text-xs mb-1 block">Line / Name</label>
            <input
              type="text"
              value={line}
              onChange={e => setLine(e.target.value)}
              placeholder="e.g., Hemingway"
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-amber-500"
            />
          </div>
        </div>

        {/* Strength */}
        <div>
          <label className="text-white/60 text-xs mb-2 block">Strength</label>
          <div className="grid grid-cols-4 gap-2">
            {STRENGTH_OPTIONS.map(s => (
              <button
                key={s}
                onClick={() => setStrength(s)}
                className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                  strength === s
                    ? 'bg-amber-500 text-stone-900'
                    : 'bg-white/5 text-white/70 hover:bg-white/10'
                }`}
              >
                {s.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </button>
            ))}
          </div>
        </div>

        {/* Flavors */}
        <div>
          <label className="text-white/60 text-xs mb-2 block">Flavors</label>
          <div className="flex flex-wrap gap-2">
            {FLAVOR_OPTIONS.map(f => (
              <button
                key={f}
                onClick={() => toggleFlavor(f)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  flavors.includes(f)
                    ? 'bg-amber-500 text-stone-900'
                    : 'bg-white/5 text-white/70 hover:bg-white/10'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Score */}
        <div>
          <label className="text-white/60 text-xs mb-2 block">Score: {score}/100</label>
          <input
            type="range"
            min="0"
            max="100"
            value={score}
            onChange={e => setScore(parseInt(e.target.value))}
            className="w-full accent-amber-500"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="text-white/60 text-xs mb-1 block">Notes</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Your thoughts on this cigar..."
            rows={4}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-amber-500 resize-none"
          />
        </div>

        {/* Would Buy Again */}
        <button
          onClick={() => setWouldBuyAgain(!wouldBuyAgain)}
          className={`w-full py-3 rounded-xl border flex items-center justify-center gap-2 transition-colors ${
            wouldBuyAgain
              ? 'bg-amber-500/20 border-amber-500/50 text-amber-500'
              : 'bg-white/5 border-white/10 text-white/50'
          }`}
        >
          <Heart className={`w-5 h-5 ${wouldBuyAgain ? 'fill-current' : ''}`} />
          Would buy again
        </button>
      </div>
    </div>
  );
};

// ============ NOTE DETAIL VIEW ============
const NoteDetailView: React.FC<{
  note: TastingNote;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ note, onBack, onEdit, onDelete }) => (
  <div className="flex-1 overflow-y-auto pb-20">
    {note.photoCigar && getPhotoUrl(note.photoCigar) && (
      <div
        className="h-48 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${getPhotoUrl(note.photoCigar)})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900 to-transparent" />
      </div>
    )}

    <div className="sticky top-0 z-10 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
      <div className="flex items-center justify-between px-4 py-3">
        <button onClick={onBack} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <div className="flex items-center gap-2">
          <button onClick={onEdit} className="p-2 hover:bg-white/10 rounded-lg">
            <Edit className="w-5 h-5 text-white" />
          </button>
          <button onClick={onDelete} className="p-2 hover:bg-white/10 rounded-lg">
            <Trash2 className="w-5 h-5 text-red-400" />
          </button>
        </div>
      </div>
    </div>

    <div className="p-4 space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-white">{note.brand}</h1>
          {note.line && <p className="text-white/60">{note.line}</p>}
        </div>
        {note.overallScore && (
          <div className="flex items-center gap-1 text-amber-500 text-xl">
            <Star className="w-6 h-6 fill-current" />
            <span className="font-bold">{note.overallScore}</span>
          </div>
        )}
      </div>

      {note.overallStrength && (
        <div className="inline-block px-3 py-1 rounded-full bg-amber-500/20 text-amber-500 text-sm font-medium">
          {note.overallStrength.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </div>
      )}

      {note.flavors.length > 0 && (
        <div>
          <h3 className="text-white/40 text-xs uppercase mb-2">Flavors</h3>
          <div className="flex flex-wrap gap-2">
            {note.flavors.map(f => (
              <span key={f} className="px-3 py-1 rounded-full bg-white/10 text-white text-sm">
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </span>
            ))}
          </div>
        </div>
      )}

      {note.notes && (
        <div>
          <h3 className="text-white/40 text-xs uppercase mb-2">Notes</h3>
          <p className="text-white/80">{note.notes}</p>
        </div>
      )}

      <div className="flex items-center gap-4 text-white/50 text-sm">
        <span>{note.smokeDate}</span>
        {note.wouldBuyAgain && (
          <span className="flex items-center gap-1 text-amber-500">
            <Heart className="w-4 h-4 fill-current" /> Would buy again
          </span>
        )}
      </div>
    </div>
  </div>
);

// ============ STATS VIEW ============
const StatsView: React.FC<{
  stats: any;
  loading: boolean;
  onBack: () => void;
}> = ({ stats, loading, onBack }) => (
  <div className="flex-1 overflow-y-auto pb-20">
    <div className="sticky top-0 z-10 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
      <div className="flex items-center gap-2 px-4 py-3">
        <button onClick={onBack} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <h1 className="text-lg font-semibold text-white">Statistics</h1>
      </div>
    </div>

    <div className="p-4">
      {loading || !stats ? (
        <div className="text-center py-12 text-white/50">Loading stats...</div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-amber-500">{stats.totalNotes}</div>
              <div className="text-white/50 text-sm">Total Notes</div>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-amber-500">{stats.averageScore || '-'}</div>
              <div className="text-white/50 text-sm">Avg Score</div>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-amber-500">{stats.uniqueBrands}</div>
              <div className="text-white/50 text-sm">Brands Tried</div>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-amber-500">${stats.totalSpent || 0}</div>
              <div className="text-white/50 text-sm">Total Spent</div>
            </div>
          </div>

          {stats.topFlavors?.length > 0 && (
            <div>
              <h3 className="text-white font-medium mb-3">Top Flavors</h3>
              <div className="flex flex-wrap gap-2">
                {stats.topFlavors.map((f: any) => (
                  <span key={f.flavor} className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-500 text-sm">
                    {f.flavor} ({f.count})
                  </span>
                ))}
              </div>
            </div>
          )}

          {stats.topBrands?.length > 0 && (
            <div>
              <h3 className="text-white font-medium mb-3">Top Brands</h3>
              <div className="space-y-2">
                {stats.topBrands.map((b: any) => (
                  <div key={b.brand} className="flex justify-between items-center bg-white/5 rounded-lg p-3">
                    <span className="text-white">{b.brand}</span>
                    <span className="text-amber-500">{b.count} cigars</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
);

// ============ FESTIVALS LIST VIEW ============
const FestivalsListView: React.FC<{
  festivals: Festival[];
  onSelectFestival: (festivalId: string) => void;
  onBack: () => void;
}> = ({ festivals, onSelectFestival, onBack }) => (
  <div className="flex-1 overflow-y-auto pb-20">
    <div className="sticky top-0 z-10 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
      <div className="flex items-center gap-2 px-4 py-3">
        <button onClick={onBack} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <h1 className="text-lg font-semibold text-white">Cigar Festivals</h1>
      </div>
    </div>

    <div className="p-4 space-y-4">
      {festivals.map(festival => {
        const vendorCount = SAMPLE_VENDORS.filter(v => v.festivalId === festival.id).length;
        return (
          <button
            key={festival.id}
            onClick={() => onSelectFestival(festival.id)}
            className="w-full p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-left"
          >
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Map className="w-8 h-8 text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-lg">{festival.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <MapPin className="w-3.5 h-3.5 text-white/40" />
                  <span className="text-white/50 text-sm">{festival.location}</span>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  {festival.date && (
                    <span className="px-2 py-1 rounded-lg bg-amber-500/10 text-amber-500 text-xs font-medium">
                      {festival.date}
                    </span>
                  )}
                  {vendorCount > 0 && (
                    <span className="px-2 py-1 rounded-lg bg-white/10 text-white/60 text-xs font-medium">
                      {vendorCount} vendors
                    </span>
                  )}
                </div>
                <p className="text-white/40 text-sm mt-2 line-clamp-2">{festival.description}</p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

// ============ MAIN APP ============
function App() {
  const { user, loading: authLoading, login, loginWithGoogle, register, logout, continueAsGuest } = useAuth();
  const { notes, loading: notesLoading, fetchNotes, getNote, createNote, updateNote, deleteNote } = useNotes();
  const { stats, loading: statsLoading, fetchStats } = useStats();

  const [view, setView] = useState('landing');
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [editNoteId, setEditNoteId] = useState<string | null>(null);
  const [selectedFestivalId, setSelectedFestivalId] = useState<string | null>(null);
  const [selectedNote, setSelectedNote] = useState<TastingNote | null>(null);

  // Saved items state
  const [savedQuestions, setSavedQuestions] = useState<string[]>([]);
  const [savedCigarIds, setSavedCigarIds] = useState<string[]>([]);
  const [savedVendorIds, setSavedVendorIds] = useState<string[]>([]);
  const [festivalCheckIns, setFestivalCheckIns] = useState<FestivalCheckIn[]>([]);
  const [earnedFestivalBadgeIds, setEarnedFestivalBadgeIds] = useState<string[]>([]);
  const [newFestivalBadges, setNewFestivalBadges] = useState<FestivalBadge[]>([]);
  const [festivalNotes, setFestivalNotes] = useState<FestivalNote[]>([]);
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Lounge state
  const [selectedLounge, setSelectedLounge] = useState<Lounge | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [earnedBadgeIds, setEarnedBadgeIds] = useState<string[]>([]);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [newBadgesEarned, setNewBadgesEarned] = useState<Badge[]>([]);
  const [apiLounges, setApiLounges] = useState<Lounge[]>([]);
  const [apiLoungesLoading, setApiLoungesLoading] = useState(false);
  const [loungeDetailsLoading, setLoungeDetailsLoading] = useState(false);
  const [searchRadius, setSearchRadius] = useState(25); // miles
  const [searchLocation, setSearchLocation] = useState<{ lat: number; lng: number } | null>(null); // For city search
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);

  // Cigar identification state
  const [showIdentifyResult, setShowIdentifyResult] = useState(false);
  const [identifyLoading, setIdentifyLoading] = useState(false);
  const [identifyResult, setIdentifyResult] = useState<{
    identified: boolean;
    cigarId?: string;
    brand?: string;
    line?: string;
    confidence: number;
    method: 'workers_ai' | 'claude_vision' | 'not_found';
    photoUrl?: string;
    rawResponse?: string;
  } | null>(null);

  // Voice input handler
  const handleVoiceResult = (transcript: string) => {
    const intent = parseVoiceIntent(transcript);

    switch (intent.type) {
      case 'command':
        // Check for camera identify command
        if (intent.target === 'identify' && intent.command === 'camera') {
          // Voice requested camera identification - open the voice modal with camera
          setVoiceModalOpen(true);
          return;
        }
        setView(intent.target);
        break;
      case 'search':
        // Navigate to explorer with search results
        setView('explorer');
        // Store search in localStorage for explorer to pick up
        localStorage.setItem('voice_search', JSON.stringify({
          query: intent.query,
          filters: intent.filters,
          results: intent.results.map(c => c.id),
        }));
        break;
      case 'log':
        // Navigate to new note with pre-filled data
        setView('new');
        if (intent.cigarName || intent.rating) {
          localStorage.setItem('voice_log', JSON.stringify({
            cigarName: intent.cigarName,
            rating: intent.rating,
            notes: intent.notes,
          }));
        }
        break;
      default:
        // Unknown intent - still show explorer for discovery
        setView('explorer');
    }
  };

  // Camera capture handler for cigar identification
  const handleCameraCapture = async (file: File) => {
    setVoiceModalOpen(false);
    setShowIdentifyResult(true);
    setIdentifyLoading(true);
    setIdentifyResult(null);

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await fetch('/api/identify', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json() as {
        data?: {
          identified: boolean;
          cigarId?: string;
          brand?: string;
          line?: string;
          confidence: number;
          method: 'workers_ai' | 'claude_vision' | 'not_found';
          photoUrl?: string;
          rawResponse?: string;
        };
        error?: string;
      };

      if (data.error) {
        setIdentifyResult({
          identified: false,
          confidence: 0,
          method: 'not_found',
          rawResponse: data.error,
        });
      } else if (data.data) {
        setIdentifyResult(data.data);
      }
    } catch (error) {
      console.error('Identification error:', error);
      setIdentifyResult({
        identified: false,
        confidence: 0,
        method: 'not_found',
        rawResponse: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIdentifyLoading(false);
    }
  };

  // Retry camera capture
  const handleRetryIdentify = () => {
    setShowIdentifyResult(false);
    setIdentifyResult(null);
    setVoiceModalOpen(true);
  };

  // Personalization state
  const [smokeLog, setSmokeLog] = useState<SmokeLogEntry[]>([]);
  const [tasteProfile, setTasteProfile] = useState<TasteProfile | null>(null);
  const [showLogSmokeModal, setShowLogSmokeModal] = useState(false);
  const [showSmokeLogView, setShowSmokeLogView] = useState(false);
  const [cigarToLog, setCigarToLog] = useState<Cigar | undefined>(undefined);

  // PWA hook
  const pwa = usePWA();

  // Load saved data
  useEffect(() => {
    setSavedQuestions(getSavedQuestions());
    setSavedCigarIds(getSavedCigarIds());
    setSavedVendorIds(getSavedVendorIds());
    setFestivalCheckIns(getFestivalCheckIns());
    setEarnedFestivalBadgeIds(getEarnedFestivalBadgeIds());
    setFestivalNotes(getFestivalNotes());
    setUserPreferences(getUserPreferences());
    setCheckIns(getCheckIns());
    setEarnedBadgeIds(getEarnedBadgeIds());
    setSmokeLog(getSmokeLog());
  }, []);

  // Build taste profile when relevant data changes
  useEffect(() => {
    const profile = buildTasteProfile(notes, savedCigarIds, smokeLog);
    setTasteProfile(profile);
  }, [notes, savedCigarIds, smokeLog]);

  // Toggle vendor bookmark
  const toggleVendorBookmark = (vendorId: string) => {
    setSavedVendorIds(prev => {
      const newIds = prev.includes(vendorId)
        ? prev.filter(id => id !== vendorId)
        : [...prev, vendorId];
      saveSavedVendorIds(newIds);
      return newIds;
    });
  };

  // Check in to a festival
  const checkInToFestival = (festivalId: string, note?: string) => {
    const newCheckIn: FestivalCheckIn = {
      id: `checkin-${Date.now()}`,
      festivalId,
      timestamp: new Date().toISOString(),
      note,
    };

    setFestivalCheckIns(prev => {
      const updated = [...prev, newCheckIn];
      saveFestivalCheckIns(updated);

      // Check for new badges
      const newBadges: FestivalBadge[] = [];
      const festivalCheckInCount = updated.filter((c, i, arr) =>
        arr.findIndex(x => x.festivalId === c.festivalId) === i
      ).length;

      FESTIVAL_BADGES.forEach(badge => {
        if (earnedFestivalBadgeIds.includes(badge.id)) return;

        if (badge.requirement.type === 'check_in_count' &&
            badge.requirement.count &&
            festivalCheckInCount >= badge.requirement.count) {
          newBadges.push(badge);
        }
        if (badge.requirement.type === 'specific_festival' &&
            badge.requirement.festivalId === festivalId) {
          newBadges.push(badge);
        }
      });

      if (newBadges.length > 0) {
        const newBadgeIds = [...earnedFestivalBadgeIds, ...newBadges.map(b => b.id)];
        setEarnedFestivalBadgeIds(newBadgeIds);
        saveEarnedFestivalBadgeIds(newBadgeIds);
        setNewFestivalBadges(newBadges);
      }

      return updated;
    });
  };

  // Check if already checked in to a festival today
  const isCheckedInToday = (festivalId: string) => {
    const today = new Date().toDateString();
    return festivalCheckIns.some(c =>
      c.festivalId === festivalId &&
      new Date(c.timestamp).toDateString() === today
    );
  };

  // Add a festival note (record a cigar tried at a booth)
  const addFestivalNote = (note: Omit<FestivalNote, 'id' | 'timestamp'>) => {
    const newNote: FestivalNote = {
      ...note,
      id: `note-${Date.now()}`,
      timestamp: new Date().toISOString(),
    };
    setFestivalNotes(prev => {
      const updated = [...prev, newNote];
      saveFestivalNotes(updated);
      return updated;
    });
  };

  // Delete a festival note
  const deleteFestivalNote = (noteId: string) => {
    setFestivalNotes(prev => {
      const updated = prev.filter(n => n.id !== noteId);
      saveFestivalNotes(updated);
      return updated;
    });
  };

  // Fetch data when user logs in
  useEffect(() => {
    if (user) {
      fetchNotes();
      fetchStats();
      // Check if onboarding needed
      if (!getUserPreferences() && user.id !== 'guest') {
        setShowOnboarding(true);
      } else {
        setView('dashboard');
      }
    }
  }, [user]);

  // Fetch selected note
  useEffect(() => {
    if (selectedNoteId) {
      getNote(selectedNoteId).then(note => setSelectedNote(note));
    } else {
      setSelectedNote(null);
    }
  }, [selectedNoteId]);

  // Search for lounges via Google Places API
  const searchNearbyLounges = async (lat: number, lng: number, radiusMiles: number = 25) => {
    setApiLoungesLoading(true);
    try {
      // Convert miles to meters (Google API uses meters)
      const radiusMeters = Math.round(radiusMiles * 1609.34);
      const response = await fetch(
        `/api/lounges/search?lat=${lat}&lng=${lng}&radius=${radiusMeters}&query=cigar+lounge+cigar+bar+tobacco+shop`
      );
      const data = await response.json() as {
        data?: {
          results?: Array<{
            id: string;
            name: string;
            address: string;
            location: { lat: number; lng: number };
            rating?: number;
            reviewCount?: number;
            openNow?: boolean;
          }>;
        };
      };

      if (data.data?.results) {
        // Convert API results to Lounge format
        const lounges: Lounge[] = data.data.results.map((place) => ({
          id: `gp_${place.id}`,
          googlePlaceId: place.id, // Store original Place ID for fetching details
          name: place.name,
          slug: place.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          address: place.address.split(',')[0] || place.address,
          city: place.address.split(',')[1]?.trim() || '',
          state: place.address.split(',')[2]?.trim().split(' ')[0] || '',
          zip: place.address.split(',')[2]?.trim().split(' ')[1] || '',
          country: 'USA',
          location: place.location,
          description: `Cigar lounge discovered via Google Places`,
          shortDescription: 'Nearby cigar lounge',
          hours: [],
          amenities: {
            fullBar: false,
            beerWine: false,
            food: false,
            lockers: false,
            privateRooms: false,
            outdoorArea: false,
            wifi: false,
            tvs: false,
            membersOnly: false,
            walkInFriendly: true,
            retailHumidor: true,
            cuttingService: false,
            byob: false,
          },
          specials: [],
          events: [],
          rating: place.rating,
          reviewCount: place.reviewCount,
          claimed: false,
          verified: false,
          featured: false,
          source: 'google_places' as const,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }));
        setApiLounges(lounges);
      }
    } catch (error) {
      console.error('Error fetching lounges:', error);
    } finally {
      setApiLoungesLoading(false);
    }
  };

  // Fetch full details for a Google Places lounge
  const fetchGooglePlacesDetails = async (lounge: Lounge): Promise<Lounge> => {
    if (!lounge.googlePlaceId) return lounge;

    setLoungeDetailsLoading(true);
    try {
      const response = await fetch(`/api/lounges/details?placeId=${lounge.googlePlaceId}`);
      const data = await response.json() as {
        data?: {
          phone?: string;
          website?: string;
          hours?: string[];
          openNow?: boolean;
          rating?: number;
          reviewCount?: number;
        };
      };

      if (data.data) {
        // Parse hours from Google's weekday_text format (e.g., "Monday: 10:00 AM – 10:00 PM")
        const parsedHours: LoungeHours[] = (data.data.hours || []).map((hourStr: string) => {
          const match = hourStr.match(/^(\w+):\s*(.+)$/);
          if (!match) return { day: hourStr, open: '', close: '', closed: true };

          const day = match[1];
          const timeRange = match[2];

          if (timeRange.toLowerCase() === 'closed') {
            return { day, open: '', close: '', closed: true };
          }

          const times = timeRange.split('–').map(t => t.trim());
          return {
            day,
            open: times[0] || '',
            close: times[1] || '',
            closed: false,
          };
        });

        // Return updated lounge with full details
        return {
          ...lounge,
          phone: data.data.phone || lounge.phone,
          website: data.data.website || lounge.website,
          hours: parsedHours.length > 0 ? parsedHours : lounge.hours,
          rating: data.data.rating || lounge.rating,
          reviewCount: data.data.reviewCount || lounge.reviewCount,
          description: lounge.website
            ? `Cigar lounge discovered via Google Places`
            : `Cigar lounge discovered via Google Places`,
        };
      }
    } catch (error) {
      console.error('Error fetching lounge details:', error);
    } finally {
      setLoungeDetailsLoading(false);
    }
    return lounge;
  };

  // Handle lounge selection - fetch details if from Google Places
  const handleSelectLounge = async (lounge: Lounge) => {
    if (lounge.source === 'google_places' && lounge.googlePlaceId) {
      // Fetch full details before showing
      const detailedLounge = await fetchGooglePlacesDetails(lounge);
      setSelectedLounge(detailedLounge);
    } else {
      setSelectedLounge(lounge);
    }
    setView('lounge-detail');
  };

  // Trigger lounge search when location or radius changes
  useEffect(() => {
    // Use searchLocation (from city search) if available, otherwise userLocation (GPS)
    const location = searchLocation || userLocation;
    if (location) {
      searchNearbyLounges(location.lat, location.lng, searchRadius);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation, searchLocation, searchRadius]);

  // Handle radius change
  const handleRadiusChange = (newRadius: number) => {
    setSearchRadius(newRadius);
  };

  // Handle city/zip search via geocoding
  const handleCitySearch = async (address: string) => {
    setApiLoungesLoading(true);
    setLocationError(null);
    try {
      const response = await fetch(`/api/lounges/geocode?address=${encodeURIComponent(address)}`);
      const data = await response.json() as {
        data?: { lat: number; lng: number; formattedAddress: string };
        error?: string;
      };

      if (data.error) {
        setLocationError(data.error === 'Location not found'
          ? `Could not find "${address}". Try a different city or zip code.`
          : 'Error searching location. Please try again.');
        return;
      }

      if (data.data) {
        setSearchLocation({ lat: data.data.lat, lng: data.data.lng });
        // Clear user GPS location since we're using city search now
        setUserLocation(null);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setLocationError('Error searching location. Please try again.');
    } finally {
      setApiLoungesLoading(false);
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <CigarIcon className="w-12 h-12 text-amber-500 animate-pulse" />
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return <LandingView onLogin={login} onRegister={register} onGoogleLogin={loginWithGoogle} onGuest={continueAsGuest} />;
  }

  // Onboarding quiz
  if (showOnboarding) {
    return (
      <PreferencesQuizView
        existingPreferences={userPreferences || undefined}
        onComplete={(prefs) => {
          saveUserPreferences(prefs);
          setUserPreferences(prefs);
          setShowOnboarding(false);
          setView('dashboard');
        }}
        onSkip={() => {
          setShowOnboarding(false);
          setView('dashboard');
        }}
      />
    );
  }

  // Get selected festival
  const selectedFestival = selectedFestivalId ? FESTIVALS.find(f => f.id === selectedFestivalId) : null;

  // Get saved cigars as full objects
  const savedCigars = SAMPLE_RECOMMENDATIONS.filter(c => savedCigarIds.includes(c.id));

  // Handlers
  const handleSaveQuestion = (question: string) => {
    const updated = [...savedQuestions, question];
    setSavedQuestions(updated);
    saveSavedQuestions(updated);
  };

  const handleDeleteQuestion = (index: number) => {
    const updated = savedQuestions.filter((_, i) => i !== index);
    setSavedQuestions(updated);
    saveSavedQuestions(updated);
  };

  const handleSaveCigar = (cigarId: string) => {
    const updated = [...savedCigarIds, cigarId];
    setSavedCigarIds(updated);
    saveSavedCigarIds(updated);
  };

  const handleUnsaveCigar = (cigarId: string) => {
    const updated = savedCigarIds.filter(id => id !== cigarId);
    setSavedCigarIds(updated);
    saveSavedCigarIds(updated);
  };

  // Toggle save cigar from Cigar object (used by Daily Ritual and Explorer)
  const handleToggleSaveCigarFromObject = (cigar: Cigar) => {
    if (savedCigarIds.includes(cigar.id)) {
      handleUnsaveCigar(cigar.id);
    } else {
      handleSaveCigar(cigar.id);
    }
  };

  // Smoke log handlers
  const handleLogSmoke = (cigar?: Cigar) => {
    setCigarToLog(cigar);
    setShowLogSmokeModal(true);
  };

  const handleSmokeLogSave = (entry: SmokeLogEntry) => {
    setSmokeLog(prev => [entry, ...prev]);
    setShowLogSmokeModal(false);
    setCigarToLog(undefined);
  };

  const handleDeleteSmokeLogEntry = (entryId: string) => {
    deleteSmokeLogEntry(entryId);
    setSmokeLog(prev => prev.filter(e => e.id !== entryId));
  };

  // Get recently smoked cigar IDs for personalization
  const recentlySmokedIds = getRecentlySmokedIds(7);

  const handleSaveNote = async (noteData: Partial<TastingNote>) => {
    if (editNoteId) {
      await updateNote(editNoteId, noteData);
      setEditNoteId(null);
      setView('journal');
    } else {
      await createNote(noteData);
      setView('journal');
    }
  };

  const handleDeleteNote = async () => {
    if (selectedNoteId) {
      await deleteNote(selectedNoteId);
      setSelectedNoteId(null);
      setView('journal');
    }
  };

  // Lounge handlers
  const requestUserLocation = () => {
    if (!('geolocation' in navigator)) {
      setLocationError('Geolocation not supported by your browser');
      // Fallback to Naples, FL area
      setUserLocation({ lat: 26.4317, lng: -81.7962 });
      return;
    }

    setLocationLoading(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLocationLoading(false);
        setLocationError(null);
      },
      (error) => {
        console.error('Error getting location:', error);
        setLocationLoading(false);

        let errorMsg = 'Unable to get location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Location permission denied. Using default location.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Location unavailable. Using default location.';
            break;
          case error.TIMEOUT:
            errorMsg = 'Location request timed out. Using default location.';
            break;
        }
        setLocationError(errorMsg);
        // Default to Naples, FL area if location fails
        setUserLocation({ lat: 26.4317, lng: -81.7962 });
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // Cache for 5 minutes
      }
    );
  };

  const handleCheckIn = (note?: string, linkedNoteId?: string) => {
    if (!selectedLounge) return;

    const newCheckIn: CheckIn = {
      id: crypto.randomUUID(),
      userId: user?.id || 'guest',
      loungeId: selectedLounge.id,
      timestamp: new Date().toISOString(),
      note,
      linkedNoteId,
    };

    const updatedCheckIns = [...checkIns, newCheckIn];
    setCheckIns(updatedCheckIns);
    saveCheckIns(updatedCheckIns);

    // Check for new badges
    const newBadges: Badge[] = [];
    const loungeCheckIns = updatedCheckIns.filter(c => c.loungeId === selectedLounge.id);
    const uniqueLounges = new Set(updatedCheckIns.map(c => c.loungeId));
    const hour = new Date().getHours();

    // First check-in badge
    if (updatedCheckIns.length === 1 && !earnedBadgeIds.includes('first-checkin')) {
      newBadges.push(BADGES.find(b => b.id === 'first-checkin')!);
    }

    // Regular badge (10 visits to same lounge)
    if (loungeCheckIns.length === 10 && !earnedBadgeIds.includes('regular')) {
      newBadges.push(BADGES.find(b => b.id === 'regular')!);
    }

    // Explorer badge (5 unique lounges)
    if (uniqueLounges.size === 5 && !earnedBadgeIds.includes('explorer')) {
      newBadges.push(BADGES.find(b => b.id === 'explorer')!);
    }

    // Night owl badge (after 10pm)
    if (hour >= 22 && !earnedBadgeIds.includes('night-owl')) {
      newBadges.push(BADGES.find(b => b.id === 'night-owl')!);
    }

    // Early bird badge (before noon)
    if (hour < 12 && !earnedBadgeIds.includes('early-bird')) {
      newBadges.push(BADGES.find(b => b.id === 'early-bird')!);
    }

    if (newBadges.length > 0) {
      const newBadgeIds = [...earnedBadgeIds, ...newBadges.map(b => b.id)];
      setEarnedBadgeIds(newBadgeIds);
      saveEarnedBadgeIds(newBadgeIds);
      setNewBadgesEarned(newBadges);
    }
  };

  const getLoungeCheckInCount = (loungeId: string) => {
    return checkIns.filter(c => c.loungeId === loungeId).length;
  };

  const getTotalCheckIns = () => checkIns.length;

  const getEarnedBadges = () => BADGES.filter(b => earnedBadgeIds.includes(b.id));

  // Render views
  const renderView = () => {
    switch (view) {
      case 'dashboard':
        return (
          <DashboardView
            user={user}
            notes={notes}
            savedCigars={savedCigarIds.length}
            savedCigarIds={savedCigarIds}
            savedQuestions={savedQuestions.length}
            onNavigate={setView}
            onSelectFestival={(id) => { setSelectedFestivalId(id); setView('festival-detail'); }}
            onSaveCigar={handleToggleSaveCigarFromObject}
            onLogSmoke={handleLogSmoke}
          />
        );

      case 'advisor':
        return (
          <CigarAdvisorHub
            userPreferences={userPreferences || undefined}
            journalNotes={notes}
            festivalName={selectedFestival?.name}
            onBack={() => setView('dashboard')}
            onSaveQuestion={handleSaveQuestion}
          />
        );

      case 'journal':
        return (
          <JournalView
            notes={notes}
            loading={notesLoading}
            onSelectNote={(id) => { setSelectedNoteId(id); setView('note-detail'); }}
            onNewNote={() => { setEditNoteId(null); setView('new'); }}
            onBack={() => setView('dashboard')}
          />
        );

      case 'new':
      case 'edit':
        const editNote = editNoteId ? notes.find(n => n.id === editNoteId) : null;
        return (
          <NoteFormView
            editNote={editNote}
            onSave={handleSaveNote}
            onCancel={() => setView(editNoteId ? 'note-detail' : 'journal')}
          />
        );

      case 'note-detail':
        if (!selectedNote) return null;
        return (
          <NoteDetailView
            note={selectedNote}
            onBack={() => { setSelectedNoteId(null); setView('journal'); }}
            onEdit={() => { setEditNoteId(selectedNoteId); setView('edit'); }}
            onDelete={handleDeleteNote}
          />
        );

      case 'recommendations':
        return (
          <RecommendationsView
            savedCigarIds={savedCigarIds}
            onSaveCigar={handleSaveCigar}
            onUnsaveCigar={handleUnsaveCigar}
            onSelectCigar={(cigar) => console.log('Selected cigar:', cigar)}
            onBack={() => setView('dashboard')}
          />
        );

      case 'saved':
        return (
          <SavedItemsView
            savedQuestions={savedQuestions}
            savedCigars={savedCigars}
            onDeleteQuestion={handleDeleteQuestion}
            onDeleteCigar={handleUnsaveCigar}
            onAddCustomQuestion={(q) => handleSaveQuestion(q)}
            onViewCigar={(cigar) => console.log('View cigar:', cigar)}
            onDiscoverCigars={() => setView('recommendations')}
            onBack={() => setView('dashboard')}
          />
        );

      case 'festivals':
        return (
          <FestivalsListView
            festivals={FESTIVALS}
            onSelectFestival={(id) => { setSelectedFestivalId(id); setView('festival-detail'); }}
            onBack={() => setView('dashboard')}
          />
        );

      case 'festival-detail':
        if (!selectedFestival) {
          setView('festivals');
          return null;
        }
        return (
          <>
            <FestivalDetailView
              festival={selectedFestival}
              onBack={() => { setSelectedFestivalId(null); setView('festivals'); }}
              onViewMap={() => setView('festival-map')}
              onSelectVendor={(vendor) => console.log('Selected vendor:', vendor)}
              savedVendorIds={savedVendorIds}
              onToggleVendorBookmark={toggleVendorBookmark}
              isCheckedIn={isCheckedInToday(selectedFestival.id)}
              onCheckIn={(note) => checkInToFestival(selectedFestival.id, note)}
              festivalNotes={festivalNotes.filter(n => n.festivalId === selectedFestival.id)}
              onAddNote={(note) => addFestivalNote({ ...note, festivalId: selectedFestival.id })}
              onDeleteNote={deleteFestivalNote}
            />
            {/* Badge earned modal */}
            {newFestivalBadges.length > 0 && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/70" onClick={() => setNewFestivalBadges([])} />
                <div className="relative bg-stone-800 rounded-2xl p-6 max-w-sm w-full border border-amber-500/50 text-center">
                  <div className="text-5xl mb-4">{newFestivalBadges[0].icon}</div>
                  <h3 className="text-lg font-bold text-amber-500 mb-2">Badge Earned!</h3>
                  <p className="text-white font-medium mb-1">{newFestivalBadges[0].name}</p>
                  <p className="text-white/60 text-sm mb-4">{newFestivalBadges[0].description}</p>
                  <button
                    onClick={() => setNewFestivalBadges([])}
                    className="w-full py-2.5 bg-amber-500 text-stone-900 font-medium rounded-xl hover:bg-amber-400"
                  >
                    Awesome!
                  </button>
                </div>
              </div>
            )}
          </>
        );

      case 'festival-map':
        if (!selectedFestival) {
          setView('festivals');
          return null;
        }
        return (
          <FestivalMapView
            festival={selectedFestival}
            recommendedVendorIds={['arturo-fuente-ybor', 'padron-cigars-ybor', 'my-father-cigars-ybor']}
            onSelectVendor={(vendor) => console.log('Selected vendor:', vendor)}
            onBack={() => setView('festival-detail')}
          />
        );

      case 'stats':
        return (
          <StatsView
            stats={stats}
            loading={statsLoading}
            onBack={() => setView('dashboard')}
          />
        );

      case 'preferences':
        return (
          <PreferencesQuizView
            existingPreferences={userPreferences || undefined}
            onComplete={(prefs) => {
              saveUserPreferences(prefs);
              setUserPreferences(prefs);
              setView('dashboard');
            }}
            onSkip={() => setView('dashboard')}
          />
        );

      case 'explorer':
        return (
          <CigarExplorerView
            savedCigarIds={savedCigarIds}
            onSaveCigar={handleToggleSaveCigarFromObject}
            onBack={() => setView('dashboard')}
          />
        );

      case 'humidor':
        return tasteProfile ? (
          <MyHumidorView
            savedCigarIds={savedCigarIds}
            smokedCigarIds={recentlySmokedIds}
            tasteProfile={tasteProfile}
            onRemoveCigar={handleUnsaveCigar}
            onAddCigar={(cigar) => handleSaveCigar(cigar.id)}
            onLogSmoke={handleLogSmoke}
            onBack={() => setView('dashboard')}
          />
        ) : (
          <DashboardView
            user={user}
            notes={notes}
            savedCigars={savedCigarIds.length}
            savedCigarIds={savedCigarIds}
            savedQuestions={savedQuestions.length}
            onNavigate={setView}
            onSelectFestival={(id) => { setSelectedFestivalId(id); setView('festival-detail'); }}
            onSaveCigar={handleToggleSaveCigarFromObject}
          />
        );

      case 'lounges': {
        // Merge sample lounges with API results, avoiding duplicates by name similarity
        const allLounges = [...SAMPLE_LOUNGES];
        apiLounges.forEach(apiLounge => {
          const isDuplicate = SAMPLE_LOUNGES.some(
            sample => sample.name.toLowerCase().includes(apiLounge.name.toLowerCase().split(' ')[0]) ||
                     apiLounge.name.toLowerCase().includes(sample.name.toLowerCase().split(' ')[0])
          );
          if (!isDuplicate) {
            allLounges.push(apiLounge);
          }
        });
        return (
          <LoungeFinderView
            lounges={allLounges}
            userLocation={searchLocation || userLocation}
            locationLoading={locationLoading || apiLoungesLoading || loungeDetailsLoading}
            locationError={locationError}
            searchRadius={searchRadius}
            onSelectLounge={handleSelectLounge}
            onRequestLocation={requestUserLocation}
            onRadiusChange={handleRadiusChange}
            onCitySearch={handleCitySearch}
            onNavigate={setView}
          />
        );
      }

      case 'lounge-detail':
        if (!selectedLounge) {
          setView('lounges');
          return null;
        }
        return (
          <>
            <LoungeDetailView
              lounge={selectedLounge}
              userCheckInCount={getLoungeCheckInCount(selectedLounge.id)}
              earnedBadges={getEarnedBadges()}
              onCheckIn={() => {
                setNewBadgesEarned([]);
                setShowCheckInModal(true);
              }}
              onBack={() => {
                setSelectedLounge(null);
                setView('lounges');
              }}
              onNavigate={() => {
                const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedLounge.location.lat},${selectedLounge.location.lng}`;
                window.open(url, '_blank');
              }}
              onShare={() => {
                if (navigator.share) {
                  navigator.share({
                    title: selectedLounge.name,
                    text: `Check out ${selectedLounge.name} - ${selectedLounge.shortDescription}`,
                    url: window.location.href,
                  });
                }
              }}
              on7CLLearnMore={() => setView('7cl-promo')}
            />
            {showCheckInModal && (
              <CheckInModalView
                lounge={selectedLounge}
                onCheckIn={(note, linkedNoteId) => {
                  handleCheckIn(note, linkedNoteId);
                  setShowCheckInModal(false);
                }}
                onClose={() => setShowCheckInModal(false)}
                recentNotes={notes.slice(0, 5).map(n => ({ id: n.id, brand: n.brand, name: n.line || '' }))}
                newBadgesEarned={newBadgesEarned}
                totalCheckIns={getTotalCheckIns()}
                on7CLPromo={() => {
                  setShowCheckInModal(false);
                  setView('7cl-promo');
                }}
              />
            )}
          </>
        );

      case '7cl-promo':
        return (
          <SevenCLPromoView
            userCheckInCount={getTotalCheckIns()}
            onBack={() => setView(selectedLounge ? 'lounge-detail' : 'lounges')}
            onVisitSite={() => window.open('https://7centline.com', '_blank')}
          />
        );

      case 'city-guide':
        return (
          <CityGuideView
            onBack={() => setView('dashboard')}
          />
        );

      case 'band-gallery':
        return (
          <BandGalleryView
            onBack={() => setView('dashboard')}
          />
        );

      default:
        return (
          <DashboardView
            user={user}
            notes={notes}
            savedCigars={savedCigarIds.length}
            savedCigarIds={savedCigarIds}
            savedQuestions={savedQuestions.length}
            onNavigate={setView}
            onSelectFestival={(id) => { setSelectedFestivalId(id); setView('festival-detail'); }}
            onSaveCigar={handleToggleSaveCigarFromObject}
            onLogSmoke={handleLogSmoke}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-stone-900 flex flex-col">
      <Header
        user={user}
        onLogout={logout}
        currentView={view}
        onNavigate={setView}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenVoice={() => setVoiceModalOpen(true)}
      />
      {renderView()}
      <BottomNav currentView={view} onNavigate={setView} />
      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        pwa={pwa}
      />

      {/* Voice Input Modal */}
      <VoiceModal
        isOpen={voiceModalOpen}
        onClose={() => setVoiceModalOpen(false)}
        onResult={handleVoiceResult}
        onCameraCapture={handleCameraCapture}
      />

      {/* Cigar Identification Result */}
      <CigarIdentifyResult
        isOpen={showIdentifyResult}
        onClose={() => {
          setShowIdentifyResult(false);
          setIdentifyResult(null);
        }}
        result={identifyResult}
        isLoading={identifyLoading}
        onAddToHumidor={(cigar) => {
          handleSaveCigar(cigar.id);
          setShowIdentifyResult(false);
          setIdentifyResult(null);
        }}
        onLogSmoke={(cigar) => {
          setShowIdentifyResult(false);
          setIdentifyResult(null);
          handleLogSmoke(cigar);
        }}
        onRetry={handleRetryIdentify}
      />

      {/* Log Smoke Modal */}
      {showLogSmokeModal && (
        <LogSmokeModal
          cigar={cigarToLog}
          onClose={() => {
            setShowLogSmokeModal(false);
            setCigarToLog(undefined);
          }}
          onSave={handleSmokeLogSave}
        />
      )}

      {/* Smoke Log History View */}
      {showSmokeLogView && (
        <SmokeLogView
          entries={smokeLog}
          onDelete={handleDeleteSmokeLogEntry}
          onClose={() => setShowSmokeLogView(false)}
        />
      )}

      {/* PWA Install Prompt */}
      <InstallPrompt />
    </div>
  );
}

export default App;
